/**
 * useHashScroll — scrolls the current URL hash's target element into view.
 *
 * React Router's <Link> (and MUI Button component={RouterLink}) performs
 * client-side navigation and never triggers the browser's native "jump to
 * anchor" behavior, even when the hash in the URL changes. Sections built
 * for the single anchor-navigated homepage (HowItWorksSection,
 * EvidenceSection, HomePricingSection — see id="..." + scrollMarginTop in
 * frontend/src/components/home/*.tsx) assumed scrollIntoView-based scrolling
 * would exist; this hook wires that up.
 *
 * Polls for the target element rather than checking once: on a cold
 * full-page load to a URL that already has a hash (e.g. a bookmark or
 * shared link to `/#pricing`), Dashboard.tsx renders a loading Skeleton
 * (gated on `showLoadingSkeleton`) until auth/roadmap queries resolve, so
 * the target section doesn't exist in the DOM on first render. Because the
 * hash never changes again after that, a single-check effect would never
 * retry once the real content mounts. Polling handles both cases with the
 * same mechanism: if the element already exists (in-app click nav), it
 * scrolls on the very first check; if it appears later (cold load past a
 * skeleton), it scrolls as soon as it shows up; if it never appears (stale
 * hash), it gives up after MAX_ATTEMPTS instead of polling forever.
 *
 * Uses `behavior: "auto"` (instant), not "smooth": confirmed empirically
 * (live browser, cold load) that a smooth scroll issued this early — while
 * fonts/images/subsequent renders are still settling the page's layout —
 * gets silently cancelled by the browser mid-animation, leaving scrollY at
 * 0 with no error. An instant jump isn't a multi-frame animation, so it
 * can't be interrupted the same way.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const MAX_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 100;

export function useHashScroll() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.slice(1);
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        timer = setTimeout(tryScroll, POLL_INTERVAL_MS);
      }
    };

    tryScroll();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [hash]);
}
